// Resilient client-side persistence for in-progress map editing.
// Survives crashes, refreshes, and offline saves so the user never loses work.

const DRAFT_PREFIX = 'dz:draft:'
const RETRY_PREFIX = 'dz:retry:'

const pickStore = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      // Probe — Safari private mode rejects writes.
      const probe = '__dz_probe__'
      window.localStorage.setItem(probe, '1')
      window.localStorage.removeItem(probe)
      return window.localStorage
    }
  } catch (e) {
    // fall through to sessionStorage
  }
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return window.sessionStorage
    }
  } catch (e) {
    // no storage available
  }
  return null
}

const safeWrite = (store, key, value) => {
  if (!store) return false
  try {
    store.setItem(key, value)
    return true
  } catch (e) {
    // QuotaExceededError — try sessionStorage as a last resort.
    try {
      if (
        typeof window !== 'undefined' &&
        store !== window.sessionStorage &&
        window.sessionStorage
      ) {
        window.sessionStorage.setItem(key, value)
        return true
      }
    } catch (_) {
      // give up — caller will know via return value
    }
    return false
  }
}

const safeRead = key => {
  const store = pickStore()
  if (!store) return null
  try {
    const raw = store.getItem(key)
    if (raw == null) {
      // also peek into sessionStorage in case we spilled over earlier
      if (typeof window !== 'undefined' && window.sessionStorage) {
        return window.sessionStorage.getItem(key)
      }
      return null
    }
    return raw
  } catch (e) {
    return null
  }
}

const safeRemove = key => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(key)
    }
  } catch (e) {
    /* ignore */
  }
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.removeItem(key)
    }
  } catch (e) {
    /* ignore */
  }
}

export const draftKey = scope => `${DRAFT_PREFIX}${scope}`
export const retryKey = scope => `${RETRY_PREFIX}${scope}`

export const saveDraft = (scope, value) => {
  const payload = JSON.stringify({ value, updatedAt: Date.now() })
  return safeWrite(pickStore(), draftKey(scope), payload)
}

export const loadDraft = scope => {
  const raw = safeRead(draftKey(scope))
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

export const clearDraft = scope => safeRemove(draftKey(scope))

export const enqueueRetry = (scope, payload) => {
  const wrapped = JSON.stringify({ payload, queuedAt: Date.now() })
  return safeWrite(pickStore(), retryKey(scope), wrapped)
}

export const peekRetry = scope => {
  const raw = safeRead(retryKey(scope))
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

export const dequeueRetry = scope => safeRemove(retryKey(scope))

// List all draft scopes currently persisted (across both stores). Used for
// recovery prompts on mount.
export const listDraftScopes = (prefix = '') => {
  const wanted = `${DRAFT_PREFIX}${prefix}`
  const out = new Set()
  const walk = store => {
    if (!store) return
    try {
      for (let i = 0; i < store.length; i += 1) {
        const k = store.key(i)
        if (k && k.indexOf(wanted) === 0) {
          out.add(k.slice(DRAFT_PREFIX.length))
        }
      }
    } catch (e) {
      /* ignore */
    }
  }
  if (typeof window !== 'undefined') {
    walk(window.localStorage)
    walk(window.sessionStorage)
  }
  return Array.from(out)
}
